import { CommonModule, DatePipe } from '@angular/common';
import { NgModule, Type } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ElementsModule } from '@app/components/elements/elements.module';
import { WidgetsComponentsModule } from '@app/components/kit/widgets/widgets-components.module';
import { WorkflowDirectivesModule } from '@app/workflow/directives/workflow-directives.module';
import { CompactViewComponent } from '@workflow/components/navigator/compact-view/compact-view.component';
import { DevNullComponent } from '@workflow/components/navigator/dev-null.component';
import { WizardViewComponent } from '@workflow/components/navigator/wizard-view/wizard-view.component';
import { WorkflowUrlNavigatorComponent } from '@workflow/components/navigator/workflow-url-navigator.component';
import { OverviewItemComponent } from '@workflow/components/questionnaire/overview-item/overview-item.component';
import { QuestionnaireBlockQuestionComponent } from '@workflow/components/questionnaire/questionnaire-block-question.component';
import { MultipleChoiceComponent } from '@workflow/components/questionnaire/questionnaire-item/multiple-choice/multiple-choice.component';
import { RangeQuestionComponent } from '@workflow/components/questionnaire/questionnaire-item/range-question/range-question.component';
import { SliderValueComponent } from '@workflow/components/questionnaire/questionnaire-item/slider-value/slider-value.component';
import { TextQuestionComponent } from '@workflow/components/questionnaire/questionnaire-item/text-question/text-question.component';
import { QuestionnaireComponent } from '@workflow/components/questionnaire/questionnaire.component';
import { FeatherModule } from 'angular-feather';
import { MarkdownModule } from 'ngx-markdown';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { WorkflowService } from 'src/app/workflow/services/workflow.service';
import { MessplatzService } from '../services/messplatz/messplatz.service';
import { SharedModule } from '../shared.module';
import { ResultComponent } from './components/result/result.component';
import { ProjektSidebarComponent } from './components/sidebar/projektsidebar.component';
import { ObjectRendererComponent } from './renderer/object-renderer/object-renderer.component';
import { SchemaRendererComponent } from './renderer/schema-renderer/schema-renderer.component';
import { StringRendererComponent } from './renderer/string-renderer/string-renderer.component';
import { ISpecialQuestion, SPECIAL_QUESTION_DI_TOKEN } from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import {
	AkustischeReaktion,
	AkustischeReaktionComponent,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/akustische-reaktion/akustische-reaktion.component';
import {
	ColorWordTest,
	ColorWordTestComponent,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/color-word-test/color-word-test.component';
import {
	DigitalerHoerverlust,
	DigitalerHoerverlustComponent,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/digitaler-hoerverlust/digitaler-hoerverlust.component';
import {
	LabyrinthTest,
	LabyrinthTestComponent,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/labyrinth-test/labyrinth-test.component';
import { LandoltTest, LandoltTestComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/landolt-test/landolt-test.component';
import {
	OptischeFolgereaktion,
	OptischeFolgereaktionComponent,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/optische-folgereaktion/optische-folgereaktion.component';
import {
	OptischeReaktion,
	OptischeReaktionComponent,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/optische-reaktion/optische-reaktion.component';
import { TappingTest, TappingTestComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/tapping-test/tapping-test.component';
import {
	VisuomotorischeKoordination,
	VisuomotorischeKoordinationComponent,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/visuomotorische-koordination/visuomotorische-koordination.component';
import { SpecialQuestionComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.component';
import { HotWireComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/visuomotorische-koordination/hot-wire/hot-wire.component';
import { VisualNotificationComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/optische-reaktion/visual-notification.component';
import { AlternateInputModalComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/alternate-input-modal/alternate-input-modal.component';
import { MissingValueButtonComponent } from '@workflow/components/navigator/missing-value-button/missing-value-button.component';
import { MissingValueModalComponent } from '@workflow/components/navigator/missing-value-button/missing-value-modal.component';
import { WizardMissingValueButtonComponent } from '@workflow/components/navigator/missing-value-button/wizard-missing-value-button.component';
import { MissingValueIconComponent } from '@workflow/components/missing-value/missing-value-icon.component';
import { MissingValueListItemComponent } from '@workflow/components/navigator/missing-value-button/missing-value-list-item.component';
import { AkustischeReaktionEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/akustische-reaktion/akustische-reaktion-epilog.component';
import { ColorWordEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/color-word-test/color-word-epilog.component';
import { DigitalerHoerverlustEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/digitaler-hoerverlust/digitaler-hoerverlust-epilog.component';
import { LabyrinthTestEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/labyrinth-test/labyrinth-test-epilog.component';
import { OptischeFolgereaktionEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/optische-folgereaktion/optische-folgereaktion-epilog.component';
import { OptischeReaktionEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/optische-reaktion/optische-reaktion-epilog.component';
import { TappingTestEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/tapping-test/tapping-test-epilog.component';
import { VisuomotorischeKoordinationEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/visuomotorische-koordination/visuomotorische-koordination-epilog.component';
import { SpecialQuestionEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question-epilog.component';
import { WizardMissingValueButtonElementComponent } from '@workflow/components/navigator/missing-value-button/wizard-missing-value-button-element.component';
import { LandoltTestEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/landolt-test/landolt-test-epilog.component';
import { TextAreaQuestionComponent } from '@workflow/components/questionnaire/questionnaire-item/text-area-question/text-area-question.component';
import { LabelQuestionComponent } from 'src/app/workflow/components/questionnaire/questionnaire-item/label-question/label-question.component';
import { StepButtonComponent } from './components/step-button/step-button.component';
import { ParticipantTimelineComponent } from '../components/participant-timeline/participant-timeline.component';
import { WorkflowStepUrlPipe } from './pipes/workflow-step-url.pipe';
import { StepsComponent } from './components/steps/steps.component';
import { WorkflowRandomizationComponent } from '@workflow/components/randomization/workflow-randomization.component';
import { QueriesModule } from '@workflow/queries/queries.module';
import { DateQuestionComponent } from './components/questionnaire/questionnaire-item/date-question/date-question.component';

/**
 * Alle SpecialQuestions
 */
const SPECIAL_QUESTIONS: Type<ISpecialQuestion>[] = [
	AkustischeReaktion,
	ColorWordTest,
	DigitalerHoerverlust,
	LabyrinthTest,
	LandoltTest,
	OptischeFolgereaktion,
	OptischeReaktion,
	TappingTest,
	VisuomotorischeKoordination,
];

@NgModule({
	declarations: [
		DevNullComponent,
		ObjectRendererComponent,
		ProjektSidebarComponent,
		ResultComponent,
		SchemaRendererComponent,
		StringRendererComponent,
		WorkflowUrlNavigatorComponent,
		WorkflowRandomizationComponent,
		CompactViewComponent,
		WizardViewComponent,
		MissingValueButtonComponent,
		MissingValueModalComponent,
		WizardMissingValueButtonComponent,
		WizardMissingValueButtonElementComponent,
		MissingValueIconComponent,
		MissingValueListItemComponent,
		MultipleChoiceComponent,
		OverviewItemComponent,
		QuestionnaireComponent,
		QuestionnaireBlockQuestionComponent,
		RangeQuestionComponent,
		SliderValueComponent,
		TextQuestionComponent,
		TextAreaQuestionComponent,
		LabelQuestionComponent,
		AkustischeReaktionComponent,
		AkustischeReaktionEpilogComponent,
		SpecialQuestionComponent,
		SpecialQuestionEpilogComponent,
		LandoltTestComponent,
		LandoltTestEpilogComponent,
		OptischeReaktionComponent,
		OptischeReaktionEpilogComponent,
		OptischeFolgereaktionComponent,
		OptischeFolgereaktionEpilogComponent,
		DigitalerHoerverlustComponent,
		DigitalerHoerverlustEpilogComponent,
		TappingTestComponent,
		TappingTestEpilogComponent,
		VisuomotorischeKoordinationComponent,
		VisuomotorischeKoordinationEpilogComponent,
		HotWireComponent,
		LabyrinthTestComponent,
		LabyrinthTestEpilogComponent,
		ColorWordTestComponent,
		ColorWordEpilogComponent,
		VisualNotificationComponent,
		AlternateInputModalComponent,
		WorkflowStepUrlPipe,
		StepsComponent,
		StepButtonComponent,
		ParticipantTimelineComponent,
		DateQuestionComponent,
	],
	imports: [
		CommonModule,
		FeatherModule,
		FormsModule,
		PerfectScrollbarModule,
		SharedModule,
		ElementsModule,
		MarkdownModule,
		WidgetsComponentsModule,
		WorkflowDirectivesModule,
		QueriesModule
	],
	exports: [
		ProjektSidebarComponent,
		SchemaRendererComponent,
		ResultComponent,
		QuestionnaireBlockQuestionComponent,
		OverviewItemComponent,
		SpecialQuestionComponent,
		AlternateInputModalComponent,
		MissingValueIconComponent,
		ParticipantTimelineComponent,
	],
	providers: [
		WorkflowService,
		MessplatzService,
		...SPECIAL_QUESTIONS.map(specialQuestion => ({ provide: SPECIAL_QUESTION_DI_TOKEN, useClass: specialQuestion, multi: true })),
		DatePipe,
	],
})
export class WorkflowModule { }
